import { useState, type MouseEvent, type ReactNode } from "react"
import { Meeting } from "@/schema"
import { useGlobalSlideoverStore } from "@/stores/use-global-slideover-store"
import { badgeVariants, Button } from "@ui/shad"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@ui/tab-pages"
import { cn } from "@ui/utils"
import { ExternalLink, MessageSquare, SlidersHorizontal, Trash2 } from "lucide-react"
import { toast } from "sonner"

import { useMeeting } from "@/hooks/api/meetings"
import { useDeleteMeeting } from "@/hooks/api/meetings/mutations"
import { Link } from "@/components/book-link"
import { CommentSection } from "@/components/comments"
import { ConfirmDialog } from "@/components/dialogs"
import { hasContent, isMeetingPast, MeetingPastView, MeetingPrepView } from "@/components/meetings/recap"
import MeetingReviewPanel from "@/components/meetings/recap/meeting-review-panel"
import { SummaryNotation } from "@/components/meetings/summary-notation"

interface MeetingDetailsPanelProps {
  meeting: Meeting
  onDelete: () => void
  className?: string
  showBookInfo?: boolean
  initialTab?: "overview" | "comments"
}

export default function MeetingDetailsPanel({
  meeting,
  onDelete,
  className,
  showBookInfo,
  initialTab = "overview",
}: MeetingDetailsPanelProps) {
  const meetingId = meeting.id
  const { data: meetingData, isLoading: meetingLoading } = useMeeting(meetingId)
  const { mutateAsync: deleteMeeting } = useDeleteMeeting()
  const isSlideoverOpen = useGlobalSlideoverStore((s) => s.isOpen)
  const closeSlideover = useGlobalSlideoverStore((s) => s.closeSlideover)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [commentCount, setCommentCount] = useState(meeting.comments_count ?? 0)

  const handleExternalLinkClick = (e: MouseEvent) => {
    if (isSlideoverOpen && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) closeSlideover()
  }

  const handleDeleteMeeting = () => {
    deleteMeeting(meetingId).then(() => {
      toast.success("Meeting deleted")
      onDelete()
    })
    setShowDeleteConfirm(false)
  }

  const isPast = meetingData ? isMeetingPast(meetingData) : isMeetingPast(meeting)
  const meetingHasContent = meetingData ? hasContent(meetingData) : false

  let overviewContent: ReactNode

  if (!isPast) {
    overviewContent = (
      <div className="w-full max-w-full">
        <Link href={`/meetings/${meetingId}`} className="absolute top-2 right-2 z-10" onClick={handleExternalLinkClick}>
          <Button variant="ghost" size="iconXs">
            <ExternalLink />
          </Button>
        </Link>
        <MeetingPrepView meeting={meetingData} isLoading={meetingLoading} skinny showBookInfo={showBookInfo} />
      </div>
    )
  } else if (!meetingHasContent) {
    overviewContent = (
      <div className="w-full max-w-full">
        <MeetingPastView
          meeting={meetingData}
          isLoading={meetingLoading}
          skinny
          showBookInfo={showBookInfo}
          onExternalLinkClick={handleExternalLinkClick}
        />
      </div>
    )
  } else if (meetingData?.meeting_source === "imported" && meetingData?.ai_summary?.html) {
    overviewContent = (
      <div>
        <div className="my-4 text-xl font-bold">{meetingData.title}</div>
        <p className="text-tertiary-foreground mb-2 border-b pb-0.5 text-sm font-semibold">Previous CRM note</p>
        <div
          dangerouslySetInnerHTML={{
            __html: meetingData.ai_summary.html || "",
          }}
          className="prose-lexical"
        />
        <SummaryNotation aiNotice={meetingData.ai_notice} />
      </div>
    )
  } else {
    overviewContent = (
      <>
        <div className="absolute top-2 right-2 z-10 flex gap-2">
          <Link href={`/meetings/${meetingId}`} onClick={handleExternalLinkClick}>
            <Button variant="ghost" size="iconXs">
              <ExternalLink />
            </Button>
          </Link>
          {meetingData?.can_delete && (
            <Button variant="ghost" size="iconXs" onClick={() => setShowDeleteConfirm(true)}>
              <Trash2 />
            </Button>
          )}
        </div>
        <MeetingReviewPanel meeting={meetingData} isLoading={meetingLoading} skinny />
      </>
    )
  }

  return (
    <div className={className}>
      <Tabs defaultValue={initialTab} className="flex-1">
        <TabsList className="px-0">
          <TabsTrigger value="overview" icon={<SlidersHorizontal />}>
            Overview
          </TabsTrigger>
          <TabsTrigger value="comments" icon={<MessageSquare />}>
            <span className="inline-flex items-center gap-1.5">
              <span>Comments</span>
              {commentCount > 0 && (
                <span className={cn(badgeVariants({ variant: "secondary", size: "xs" }))}>{commentCount}</span>
              )}
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" disableScrollArea forceMount>
          {overviewContent}
        </TabsContent>
        <TabsContent value="comments" className="flex flex-col p-4" disableScrollArea forceMount>
          <CommentSection resourceType="Meeting" resourceId={meetingId} onCommentCountChange={setCommentCount} />
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title={"Delete meeting?"}
        description={"This will permanently delete the meeting and all its data. This action cannot be undone."}
        dismiss={"explicit"}
        cancel={{ onClick: () => setShowDeleteConfirm(false) }}
        action={{ label: "Delete", onClick: handleDeleteMeeting }}
      />
    </div>
  )
}
